// src/pages/AuthFinish.tsx
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  isSignInWithEmailLink,
  signInWithEmailLink,
  EmailAuthProvider,
  linkWithCredential,
} from "firebase/auth";
import { auth } from "../../../config/firebase";
import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import { initI18n } from "components/i18n";
import SubscriptionService from "../../../services/SubscriptionService";
import SubscriptionVerificationService from "../../../services/SubscriptionVerificationService";
import { isDevEnvironment } from "../../../env/runtimeEnv";
import { getAccount, recordLogin } from "../../../api/accountApi";
import { getOwnMembership } from "../../../api/teamMemberApi";
import { getOrganization } from "../../../api/organizationApi";
import * as Sentry from "@sentry/react";

let isAuthenticating = false;

export default function AuthFinish() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setMoods } = useAppState();
  const { setUserId } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const hasExecuted = useRef(false);

  // fetchMoods removed - UserContext now handles mood loading to avoid race conditions

  useEffect(() => {
    if (hasExecuted.current || isAuthenticating) {
      console.log(
        "🔒 Authentication already in progress or executed, skipping"
      );
      return;
    }

    hasExecuted.current = true;
    isAuthenticating = true;

    const handleSignIn = async () => {
      const href = window.location.href;

      if (auth.currentUser) {
        console.log(
          "🔒 User already authenticated before processing, redirecting to home"
        );
        setIsProcessing(false);

        console.log("we go home : 0");

        navigate("/app/home", { replace: true });
        isAuthenticating = false;
        return;
      }

      try {
        const url = new URL(href);
        const oobCode = url.searchParams.get("oobCode");
        if (oobCode) {
          const key = `consumed_oob_${oobCode}`;
          const globalKey = `auth_processed_${oobCode}`;

          if (sessionStorage.getItem(key) || localStorage.getItem(globalKey)) {
            console.log("🔒 Magic link already processed, redirecting to home");
            setIsProcessing(false);
            console.log("we go home : 2");
            navigate("/app/home", { replace: true });
            return;
          }

          sessionStorage.setItem(key, "1");
          localStorage.setItem(globalKey, "1");
          console.log("🔒 Marked magic link as processed:", oobCode);
        } else {
          console.log("🔒 No magic link parameters found");
          if (auth.currentUser) {
            console.log("🔒 User already authenticated, redirecting to home");
            setIsProcessing(false);
            isAuthenticating = false;
            console.log("we go home : 3");
            navigate("/app/home", { replace: true });
            return;
          }
          setIsProcessing(false);
          isAuthenticating = false;
          navigate("/app/login", { replace: true });
          return;
        }
      } catch (error) {
        console.warn("Error checking session storage:", error);
        // If we can't parse the URL, check if user is authenticated
        if (auth.currentUser) {
          console.log("🔒 User already authenticated, redirecting to home");
          setIsProcessing(false);
          isAuthenticating = false;
          console.log("we go home : 4");
          navigate("/app/home", { replace: true });
          return;
        }
        // Otherwise redirect to login
        setIsProcessing(false);
        isAuthenticating = false;
        navigate("/app/login", { replace: true });
        return;
      }

      if (auth.currentUser) {
        console.log(
          "🔒 User already authenticated, cleaning URL and redirecting"
        );
        // Clean up URL parameters immediately
        try {
          const url = new URL(href);
          if (url.searchParams.get("oobCode")) {
            url.searchParams.delete("oobCode");
            url.searchParams.delete("mode");
            url.searchParams.delete("apiKey");
            url.searchParams.delete("lang");
            window.history.replaceState({}, document.title, url.toString());
          }
        } catch {}
        setIsProcessing(false);
        console.log("we go home : 5");
        navigate("/app/home", { replace: true });
        return;
      }

      /*
      if (!navigator.onLine) {
        // Don't attempt to consume magic link while offline; route to login/offline
        setError("You are offline. Please reconnect to finish sign-in.");
        setIsProcessing(false);
        return;
      }
       */

      const emailFromParams = params.get("email");
      const emailFromLocalStorage = localStorage.getItem("emailForSignIn");
      const hasEmail = emailFromParams || emailFromLocalStorage;

      let isValidLink = false;
      try {
        isValidLink = isSignInWithEmailLink(auth, href);
      } catch (error) {
        console.warn("Error validating magic link:", error);
      }

      if (!isValidLink && !hasEmail) {
        console.log(
          "🔒 Invalid magic link and no email found, redirecting to login"
        );
        if (!isSuccess) {
          setError("Invalid magic link. Please try again.");
          setIsProcessing(false);
          setTimeout(() => navigate("/app/login"), 2000);
        }
        return;
      }

      let email =
        params.get("email") || localStorage.getItem("emailForSignIn") || "";

      /*
      if (!email) {
        email = window.prompt("Confirm your email to finish signing in") || "";
      }

      if (!email) {
        console.error("❌ No email provided");
        navigate("/app/login");
        return;
      }
       */

      try {
        let result;
        const currentUser = auth.currentUser;

        if (
          currentUser &&
          (currentUser as { isAnonymous?: boolean }).isAnonymous
        ) {
          try {
            const credential = EmailAuthProvider.credentialWithLink(
              email,
              href
            );
            result = await linkWithCredential(currentUser, credential);
          } catch (linkError: any) {
            // authentication/credential-already-in-use: email exists, sign in normally (loses anonymous data)
            if (
              linkError?.code === "authentication/credential-already-in-use" ||
              linkError?.message?.includes("credential-already-in-use")
            ) {
              result = await signInWithEmailLink(auth, email, href);
            } else {
              throw linkError;
            }
          }
        } else {
          try {
            result = await signInWithEmailLink(auth, email, href);
          } catch (signInError) {
            // If the link validation failed but we have an email, try again
            if (!isValidLink && hasEmail) {
              try {
                result = await signInWithEmailLink(auth, email, href);
              } catch (retryError) {
                // Check if it's a common error that we can handle gracefully
                if (
                  retryError instanceof Error &&
                  (retryError.message.includes(
                    "authentication/invalid-action-code"
                  ) ||
                    retryError.message.includes(
                      "authentication/expired-action-code"
                    ))
                ) {
                  if (!isSuccess) {
                    setError(
                      "Magic link has expired. Please request a new one."
                    );
                    setIsProcessing(false);
                    setTimeout(
                      () => navigate("/app/login?error=link_expired"),
                      3000
                    );
                  }
                  return;
                }
                throw retryError;
              }
            } else {
              throw signInError;
            }
          }
        }

        const user = result.user;

        const existingAccount = await getAccount();

        // Check if user is orphaned (removed from team)
        if (existingAccount?.orphaned) {
          setError(
            "Your access has been revoked. Please contact your team administrator."
          );
          setIsProcessing(false);
          return;
        }

        const isNewAccount = !existingAccount;

        // Creates the account if missing and stamps last_login_at in one call.
        const account = await recordLogin();

        await initI18n(account?.defaultLanguage || "en");
        setUserId(user.uid);
        Sentry.setUser({ id: 123, userId: user.uid });
        localStorage.setItem("tujud_uid", user.uid);

        // Sync app wrapper / push tokens and route by Stripe subscription status
        try {
          if (!SubscriptionService.isInitialized) {
            await SubscriptionService.initialize();
          }

          await SubscriptionVerificationService.identifyUser(user.uid).catch(
            () => {}
          );

          const userVerification =
            await SubscriptionVerificationService.verifySubscription({
              uid: user.uid,
              email: email || undefined,
            }).catch(() => ({ isValid: false as const }));
          const hasActive = userVerification.isValid === true;

          // New users with an active subscription get their team auto-created.
          if (isNewAccount && hasActive) {
            try {
              SubscriptionService.handlePostPurchaseActions(null, {
                uid: user.uid,
                email: user.email,
              }).catch((teamError: any) => {
                console.warn(
                  "⚠️ AuthFinish: Team creation check failed (non-fatal):",
                  teamError
                );
              });
            } catch (teamCheckError) {
              console.warn(
                "⚠️ AuthFinish: Failed to check team creation (non-fatal):",
                teamCheckError
              );
            }
          }

          // If the user doesn't have their own active subscription, check
          // whether they're on a team whose lead does.
          let hasTeamLeadActiveSubscription = false;
          if (!hasActive) {
            try {
              const membership = await getOwnMembership();
              if (membership?.organizationId) {
                const org = await getOrganization(membership.organizationId);
                if (org?.leadUid) {
                  const leadVerification =
                    await SubscriptionVerificationService.verifySubscription({
                      uid: org.leadUid,
                      email: org.leadUid,
                    }).catch(() => null);
                  hasTeamLeadActiveSubscription =
                    leadVerification?.isValid === true;
                }
              }
            } catch (teamCheckError) {
              console.error(
                "❌ AuthFinish: Error checking team lead subscription:",
                teamCheckError
              );
              // Continue with routing based on the user's own subscription only
            }
          }

          setIsSuccess(true);
          setIsProcessing(false);
          isAuthenticating = false;

          // Existing users only land on /app/home if they (or their team
          // lead) have an active subscription; new users always do - matches
          // the prior behavior of the separate new/existing-user branches.
          const shouldGoHome =
            isDevEnvironment ||
            hasActive ||
            hasTeamLeadActiveSubscription ||
            isNewAccount;

          if (shouldGoHome) {
            setTimeout(() => navigate("/app/home", { replace: true }), 1000);
          }
          return;
        } catch (e) {
          setIsSuccess(true);
          setIsProcessing(false);
          isAuthenticating = false;
          return;
        }

        // Cleanup
        localStorage.removeItem("emailForSignIn");
        // Strip magic link params from URL to avoid re-processing on refresh
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("oobCode");
          url.searchParams.delete("mode");
          url.searchParams.delete("apiKey");
          url.searchParams.delete("lang");
          window.history.replaceState({}, document.title, url.toString());
        } catch {}
      } catch (error) {
        // Don't show error if we're already in success state
        if (isSuccess) {
          isAuthenticating = false;
          return;
        }

        console.error("❌ Finish sign-in failed:", error);
        setIsProcessing(false);
        isAuthenticating = false;

        if (error instanceof Error) {
          console.error("❌ Error details:", error.message);

          // Handle specific Firebase Auth errors
          if (error.message.includes("authentication/invalid-action-code")) {
            if (!isSuccess) {
              setError(
                "Magic link has expired or already been used. Please request a new one."
              );
              setTimeout(
                () => navigate("/app/login?error=magic-link-expired"),
                3000
              );
            }
            isAuthenticating = false;
            return;
          } else if (
            error.message.includes("authentication/expired-action-code")
          ) {
            if (!isSuccess) {
              setError("Magic link has expired. Please request a new one.");
              setTimeout(
                () => navigate("/app/login?error=magic-link-expired"),
                3000
              );
            }
            isAuthenticating = false;
            return;
          } else if (error.message.includes("authentication/invalid-email")) {
            if (!isSuccess) {
              setError("Invalid email address. Please try again.");
              setTimeout(
                () => navigate("/app/login?error=invalid-email"),
                3000
              );
            }
            isAuthenticating = false;
            return;
          } else {
            if (!isSuccess) {
              setError("Sign-in failed. Please try again.");
              setTimeout(() => navigate("/app/login"), 3000);
            }
            isAuthenticating = false;
            return;
          }
        }
        if (!isSuccess) {
          setError("An unexpected error occurred. Please try again.");
          setTimeout(() => navigate("/app/login"), 3000);
        }
        isAuthenticating = false;
      }
    };

    handleSignIn();
  }, [navigate, params, setUserId, setMoods]);

  // Cleanup effect to reset global flag when component unmounts
  useEffect(() => {
    return () => {
      isAuthenticating = false;
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "20px",
        textAlign: "center",
      }}
    >
      {isProcessing && !error && !isSuccess && (
        <div>
          <h2>Signing you in...</h2>
          <p>Please wait while we complete your authentication. !</p>
        </div>
      )}
      {isSuccess && (
        <div style={{ color: "green" }}>
          <h2>Success!</h2>
          <p>You have been signed in successfully.</p>
          <p>Redirecting to home page...</p>
        </div>
      )}
      {error && (
        <div style={{ color: "red" }}>
          <h2>Authentication Error</h2>
          <p>{error}</p>
          <p>Redirecting to login page...</p>
        </div>
      )}
    </div>
  );
}
